import "@/App.css";
import './styles/mobile-fixes.css';
import './styles/premium-animations.css';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EnterpriseContextProvider } from './contexts/EnterpriseContext';
import { Toaster } from 'sonner';
import NotificationToast from './components/NotificationToast';
import LandingPage from './pages/LandingPage';
import Demo from './pages/Demo';
import InvoiceVerify from './pages/InvoiceVerify';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ModuleDetail from './pages/ModuleDetail';
import ModuleDetailV2 from './pages/ModuleDetailV2';
import AdminModules from './pages/admin/AdminModules';
import ModuleContentEditor from './pages/admin/ModuleContentEditor';
import AdminPedagogicalGovernance from './pages/admin/AdminPedagogicalGovernance';
import AdminCoaches from './pages/admin/AdminCoaches';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminCoachPayments from './pages/admin/AdminCoachPayments';
import AdminCoachingContext from './pages/admin/AdminCoachingContext';
import AdminDemoExport from './pages/admin/AdminDemoExport';
import AdminThemes from './pages/AdminThemes';
// import AdminLessonEditor from './pages/admin/AdminLessonEditor';
import TrainingDashboard from './pages/TrainingDashboard';
import PillarModules from './pages/PillarModules';
import MyOwenPath from './pages/MyOwenPath';
import CoachVocal from './pages/CoachVocal';
import EntrainementEcrit from './pages/EntrainementEcrit';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionChoice from './pages/SubscriptionChoice';
import EnterprisePricingPage from './pages/EnterprisePricingPage';
import EnterpriseChoice from './pages/EnterpriseChoice';
import EnterpriseHumanCoaching from './pages/EnterpriseHumanCoaching';
import QuoteRequestFormIA from './pages/enterprise/QuoteRequestFormIA';
import QuoteRequestFormHuman from './pages/enterprise/QuoteRequestFormHuman';
import QuoteRequestSuccess from './pages/enterprise/QuoteRequestSuccess';
import QuoteViewPage from './pages/enterprise/QuoteViewPage';
import AdminQuotesDashboard from './pages/admin/AdminQuotesDashboard';
import AdminQuoteDetail from './pages/admin/AdminQuoteDetail';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import AdminOrdersDashboard from './pages/admin/AdminOrdersDashboard';
import { CompanyEnterpriseOrdersList, CompanyEnterpriseOrderReport } from './pages/enterprise/CompanyEnterpriseOrders';
import ScrollToTop from './components/ScrollToTop';
import BodyScrollGuard from './components/BodyScrollGuard';
import StopOwenAudioOnRouteChange from './components/StopOwenAudioOnRouteChange';
import RHCoachingHumainDashboard from './pages/RHCoachingHumainDashboard';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import CompanyPurchaseSuccess from './pages/CompanyPurchaseSuccess';
import AffiliateJoinPage from './pages/AffiliateJoinPage';
import AffiliateDashboardAwwwards from './pages/AffiliateDashboardAwwwards';
import ContactPage from './pages/ContactPage';
import UserDashboard from './pages/UserDashboard';
import PremiumRoute from './components/PremiumRoute';
// 🎓 Import Diagnostic Wrapper (Qualiopi Onboarding)
import DiagnosticWrapper from './components/DiagnosticWrapper';
// 📘 Page Information au Public (Qualiopi)
import InformationPublicPage from './pages/InformationPublicPage';
// 📖 Page À Propos
import AboutPage from './pages/AboutPage';
// 📜 Page CGV
import CGVPage from './pages/CGVPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
// Marketplace
import Marketplace from './pages/Marketplace';
import CollaboratorDashboard from './pages/CollaboratorDashboard';
import MyGrowthPath from './pages/MyGrowthPath';
import CompanyEnterpriseConsumption from './pages/CompanyEnterpriseConsumption';
import CoachPublicProfile from './pages/CoachPublicProfile';
import CoachDashboard from './pages/CoachDashboard';
import CoachCollaboratorInsights from './pages/coach/CoachCollaboratorInsights';
import CollaboratorActivate from './pages/onboarding/CollaboratorActivate';
import HRActivate from './pages/hr/HRActivate';
import HRDashboard from './pages/hr/HRDashboard';
import BookingSuccess from './pages/BookingSuccess';
import MyBookings from './pages/MyBookings';
import SessionReviewPage from './pages/SessionReviewPage';
import VideoSession from './pages/VideoSession';
import CoachOnboarding from './pages/CoachOnboarding';
// P1.2 Page Notifications (Actions suggérées Owen)
import NotificationsPage from './pages/NotificationsPage';
// 🎯 CoachDIA Discovery
import Discovery from './pages/Discovery';
import DiscoveryCoachRecommendations from './pages/DiscoveryCoachRecommendations';
import MyDiscovery from './pages/MyDiscovery';
import DiscoveryDemo from './pages/DiscoveryDemo';
import TeamInsights from './pages/TeamInsights';
import TeamPulse from './pages/TeamPulse';
import { PulseTeamsList, PulseTeamDetail } from './pages/PulseTeamsDashboard';
import PulseBriefingRoom from './pages/PulseBriefingRoom';
import PulseErrorBoundary from './components/pulse/PulseErrorBoundary';
import PulseInvite from './pages/PulseInvite';
import PulseTest from './pages/PulseTest';
import PulseBatchDeepLink from './pages/PulseBatchDeepLink';
import NotFoundPage from './pages/NotFoundPage';
import DiscoveryPaywallModal from './components/DiscoveryPaywallModal';
import AdminDiscoveryOverview from './pages/admin/AdminDiscoveryOverview';
import AdminMarketplaceSettings from './pages/admin/AdminMarketplaceSettings';
import AdminPendingVerification from './pages/admin/AdminPendingVerification';
import AdminMarketplacePricing from './pages/admin/AdminMarketplacePricing';
import AdminCompliance from './pages/admin/AdminCompliance';
import VerifyComplianceSignaturePage from './pages/VerifyComplianceSignaturePage';
import VerifyCertificatePage from './pages/VerifyCertificatePage';
import VerifySignaturePage from './pages/VerifySignaturePage';
import AdminB2BLeads from './pages/admin/AdminB2BLeads';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" replace />;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }
  
  // Check if user is authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user has admin role
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-600 mb-6">
            Vous n'avez pas les privilèges administrateur nécessaires pour accéder à cette page.
          </p>
          <button
            onClick={() => window.location.href = '/training'}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};

// Home Route Component - Landing page accessible à tous
const HomeRoute = () => {
  return <LandingPage />;
};

function App() {
  return (
    <AuthProvider>
      <EnterpriseContextProvider>
      <BrowserRouter>
        <ScrollToTop />
        <BodyScrollGuard />
        <StopOwenAudioOnRouteChange />
        <DiscoveryPaywallModal />
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/demo" element={<Demo />} />
          {/* 🔓 Vérification publique facture (depuis QR code) — sans auth */}
          <Route path="/v/:invoiceId" element={<InvoiceVerify />} />
          <Route path="/a-propos" element={<AboutPage />} />
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/information-publique" element={<InformationPublicPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/discovery" element={
            <ProtectedRoute>
              <Discovery />
            </ProtectedRoute>
          } />
          <Route path="/discovery/coaches" element={
            <ProtectedRoute>
              <DiscoveryCoachRecommendations />
            </ProtectedRoute>
          } />
          <Route path="/my-discovery" element={
            <ProtectedRoute>
              <MyDiscovery />
            </ProtectedRoute>
          } />
          <Route path="/discovery/demo" element={<DiscoveryDemo />} />
          <Route path="/team-insights" element={
            <ProtectedRoute>
              <TeamPulse />
            </ProtectedRoute>
          } />
          <Route path="/team-pulse" element={
            <ProtectedRoute>
              <TeamPulse />
            </ProtectedRoute>
          } />
          <Route path="/team-pulse/teams" element={
            <ProtectedRoute>
              <PulseErrorBoundary><PulseTeamsList /></PulseErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/team-pulse/teams/:teamId" element={
            <ProtectedRoute>
              <PulseErrorBoundary><PulseTeamDetail /></PulseErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/team-pulse/teams/:teamId/briefing" element={
            <ProtectedRoute>
              <PulseErrorBoundary><PulseBriefingRoom /></PulseErrorBoundary>
            </ProtectedRoute>
          } />
          <Route path="/pulse-invite/:token" element={<PulseInvite />} />
          <Route path="/pulse-test/:token" element={<PulseTest />} />
          {/* 🔗 Deep-link email "Consulter le rapport" — résout l'accès rapport selon
              état (payé/non payé/session) et redirige proprement. JAMAIS d'écran blanc. */}
          <Route path="/pulse/batches/:batchId" element={<PulseBatchDeepLink />} />
          <Route path="/team-insights-legacy" element={
            <ProtectedRoute>
              <TeamInsights />
            </ProtectedRoute>
          } />
          <Route path="/training" element={
            <ProtectedRoute>
              <DiagnosticWrapper>
                <TrainingDashboard />
              </DiagnosticWrapper>
            </ProtectedRoute>
          } />
          {/* Iter294 — Alias canoniques pour navigation explicite premium */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DiagnosticWrapper>
                <TrainingDashboard />
              </DiagnosticWrapper>
            </ProtectedRoute>
          } />
          <Route path="/dashboard/prism" element={
            <ProtectedRoute>
              <MyDiscovery />
            </ProtectedRoute>
          } />
          <Route path="/my-owen-path" element={
            <ProtectedRoute>
              <MyOwenPath />
            </ProtectedRoute>
          } />
          {/* 🎯 P1.2 Route Notifications (Actions suggérées Owen) */}
          <Route path="/notifications" element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          } />
          <Route path="/pillar/:id" element={
            <ProtectedRoute>
              <PillarModules />
            </ProtectedRoute>
          } />
          <Route path="/module/:id" element={
            <ProtectedRoute>
              <ModuleDetailV2 />
            </ProtectedRoute>
          } />
          <Route path="/module-v2/:id" element={
            <ProtectedRoute>
              <ModuleDetailV2 />
            </ProtectedRoute>
          } />
          <Route path="/entrainement-ecrit/:moduleId" element={
            <PremiumRoute>
              <EntrainementEcrit />
            </PremiumRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute>
              <AdminModules />
            </AdminRoute>
          } />
          <Route path="/admin/modules" element={
            <AdminRoute>
              <AdminModules />
            </AdminRoute>
          } />
          <Route path="/admin/modules/:moduleId/content" element={
            <AdminRoute>
              <ModuleContentEditor />
            </AdminRoute>
          } />
          <Route path="/admin/pedagogical-governance" element={
            <AdminRoute>
              <AdminPedagogicalGovernance />
            </AdminRoute>
          } />
          <Route path="/admin/themes" element={
            <AdminRoute>
              <AdminThemes />
            </AdminRoute>
          } />
          <Route path="/admin/coaches" element={
            <AdminRoute>
              <AdminCoaches />
            </AdminRoute>
          } />
          <Route path="/admin/audit-log" element={
            <AdminRoute>
              <AdminAuditLog />
            </AdminRoute>
          } />
          <Route path="/admin/discovery-overview" element={
            <AdminRoute>
              <AdminDiscoveryOverview />
            </AdminRoute>
          } />
          <Route path="/admin/marketplace-settings" element={
            <AdminRoute>
              <AdminMarketplaceSettings />
            </AdminRoute>
          } />
          <Route path="/admin/pending-verification" element={
            <AdminRoute>
              <AdminPendingVerification />
            </AdminRoute>
          } />
          <Route path="/admin/marketplace-pricing" element={
            <AdminRoute>
              <AdminMarketplacePricing />
            </AdminRoute>
          } />
          <Route path="/admin/compliance" element={
            <AdminRoute>
              <AdminCompliance />
            </AdminRoute>
          } />
          {/* 🔐 Iter385 — Page publique de vérification d'authenticité (no auth) */}
          <Route path="/verify/:bookingId" element={<VerifySignaturePage />} />
          {/* 🎯 Iter386 Lot B — Vérification publique campagne conformité (no auth) */}
          <Route path="/verify-compliance/:campaignId" element={<VerifyComplianceSignaturePage />} />
          <Route path="/verify-certificate/:certId" element={<VerifyCertificatePage />} />
          <Route path="/admin/b2b-leads" element={
            <AdminRoute>
              <AdminB2BLeads />
            </AdminRoute>
          } />
          <Route path="/admin/invoices" element={
            <AdminRoute>
              <AdminInvoices />
            </AdminRoute>
          } />
          <Route path="/admin/coach-payments" element={
            <AdminRoute>
              <AdminCoachPayments />
            </AdminRoute>
          } />
          <Route path="/admin/coaching-context" element={
            <AdminRoute>
              <AdminCoachingContext />
            </AdminRoute>
          } />
          <Route path="/admin/demo-export" element={
            <AdminRoute>
              <AdminDemoExport />
            </AdminRoute>
          } />
          {/* Route supprimée - Quotas intégrés dans l'onglet Tarification */}
          {/* <Route path="/admin/lessons/:lessonId/edit" element={
            <AdminRoute>
              <AdminLessonEditor />
            </AdminRoute>
          } /> */}
          
          {/* 🏢 B2B: Subscription routes - Choix explicite Individuel/Entreprise */}
          <Route path="/subscription" element={
            <ProtectedRoute>
              <SubscriptionChoice />
            </ProtectedRoute>
          } />
          <Route path="/subscription/individual" element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          } />
          <Route path="/subscription/enterprise" element={
            <ProtectedRoute>
              <EnterpriseChoice />
            </ProtectedRoute>
          } />
          <Route path="/subscription/enterprise/ia" element={
            <ProtectedRoute>
              <EnterprisePricingPage />
            </ProtectedRoute>
          } />
          <Route path="/subscription/enterprise/coaching-humain" element={
            <ProtectedRoute>
              <EnterpriseHumanCoaching />
            </ProtectedRoute>
          } />
          <Route path="/subscription/enterprise/ia/quote-request" element={
            <ProtectedRoute>
              <QuoteRequestFormIA />
            </ProtectedRoute>
          } />
          <Route path="/subscription/enterprise/coaching-humain/quote-request" element={
            <ProtectedRoute>
              <QuoteRequestFormHuman />
            </ProtectedRoute>
          } />
          <Route path="/subscription/enterprise/quote-request/success" element={
            <ProtectedRoute>
              <QuoteRequestSuccess />
            </ProtectedRoute>
          } />
          {/* Public quote view via HMAC signed link (no auth) */}
          <Route path="/enterprise/quote-view" element={<QuoteViewPage />} />
          {/* Admin CRM for enterprise quotes */}
          <Route path="/admin/enterprise-quotes" element={
            <ProtectedRoute>
              <AdminQuotesDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/enterprise-quotes/:id" element={
            <ProtectedRoute>
              <AdminQuoteDetail />
            </ProtectedRoute>
          } />
          <Route path="/admin/enterprise-orders" element={
            <ProtectedRoute>
              <AdminOrdersDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/enterprise-orders/:id" element={
            <ProtectedRoute>
              <AdminOrderDetail />
            </ProtectedRoute>
          } />
          <Route path="/entreprise/commandes" element={
            <ProtectedRoute>
              <CompanyEnterpriseOrdersList />
            </ProtectedRoute>
          } />
          <Route path="/entreprise/commandes/:id" element={
            <ProtectedRoute>
              <CompanyEnterpriseOrderReport />
            </ProtectedRoute>
          } />
          <Route path="/company/:companyId/purchase-success" element={
            <ProtectedRoute>
              <CompanyPurchaseSuccess />
            </ProtectedRoute>
          } />
          
          <Route path="/payment-success" element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          } />
          <Route path="/payment-cancel" element={
            <ProtectedRoute>
              <PaymentCancel />
            </ProtectedRoute>
          } />
          <Route path="/company/coaching-humain" element={
            <ProtectedRoute>
              <RHCoachingHumainDashboard />
            </ProtectedRoute>
          } />
          <Route path="/affiliation" element={<AffiliateJoinPage />} />
          <Route path="/affiliate-dashboard" element={
            <ProtectedRoute>
              <AffiliateDashboardAwwwards />
            </ProtectedRoute>
          } />
          <Route path="/coach-vocal" element={
            <PremiumRoute>
              <CoachVocal />
            </PremiumRoute>
          } />
          <Route path="/contact" element={<ContactPage />} />
          {/* Marketplace routes */}
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/my-company-space" element={
            <ProtectedRoute><CollaboratorDashboard /></ProtectedRoute>
          } />
          <Route path="/my-growth-path" element={
            <ProtectedRoute><MyGrowthPath /></ProtectedRoute>
          } />
          <Route path="/company-admin/enterprise-consumption" element={
            <ProtectedRoute><CompanyEnterpriseConsumption /></ProtectedRoute>
          } />
          <Route path="/marketplace/coach/:coachId" element={<CoachPublicProfile />} />
          <Route path="/devenir-coach" element={<CoachOnboarding />} />
          <Route path="/coach/dashboard" element={
            <ProtectedRoute>
              <CoachDashboard />
            </ProtectedRoute>
          } />
          <Route path="/coach/insights-clients" element={
            <ProtectedRoute>
              <CoachCollaboratorInsights />
            </ProtectedRoute>
          } />
          {/* 🚀 Sprint 2 Iter241 — Onboarding + HR */}
          <Route path="/onboarding/collab" element={<CollaboratorActivate />} />
          <Route path="/hr/activate" element={<HRActivate />} />
          <Route path="/hr/dashboard" element={
            <ProtectedRoute>
              <HRDashboard />
            </ProtectedRoute>
          } />
          <Route path="/coach/register" element={
            <ProtectedRoute>
              <CoachDashboard />
            </ProtectedRoute>
          } />
          <Route path="/booking-success" element={
            <ProtectedRoute>
              <BookingSuccess />
            </ProtectedRoute>
          } />
          <Route path="/my-bookings" element={
            <ProtectedRoute>
              <MyBookings />
            </ProtectedRoute>
          } />
          <Route path="/session/:booking_id/review" element={
            <ProtectedRoute>
              <SessionReviewPage />
            </ProtectedRoute>
          } />
          <Route path="/session/:bookingId" element={
            <ProtectedRoute>
              <VideoSession />
            </ProtectedRoute>
          } />
          <Route path="/user-dashboard" element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          } />
          
          {/* Mon Parcours - Redirige vers la page Mon Parcours Owen */}
          <Route path="/mon-parcours" element={
            <ProtectedRoute>
              <Navigate to="/my-owen-path" replace />
            </ProtectedRoute>
          } />
          {/* 🛡️ Catch-all 404 premium — DOIT rester en DERNIÈRE position.
              Évite tout écran blanc résiduel sur URL invalide / deep-link cassé. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <NotificationToast />
      </BrowserRouter>
      </EnterpriseContextProvider>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
